/**
 * Kernbegrippen van het spel, gedeeld door regelkernel, server en client.
 *
 * Bron: ~/cdrom-rip/kennisbank/02-spelregels.md
 */
import { z } from 'zod';

/** De zes grondstoffen. De volgorde volgt de originele engine-ID's (zie kennisbank 03). */
export const RESOURCES = ['wol', 'erts', 'hout', 'goud', 'graan', 'klei'] as const;
export const ResourceSchema = z.enum(RESOURCES);
export type Resource = z.infer<typeof ResourceSchema>;

/** Landschapstypen, elk gekoppeld aan één grondstof. */
export const LANDSCAPES = [
  'weide', // wol
  'bergen', // erts
  'bos', // hout
  'rivierbedding', // goud
  'akker', // graan
  'heuvels', // klei
] as const;
export const LandscapeSchema = z.enum(LANDSCAPES);
export type Landscape = z.infer<typeof LandscapeSchema>;

export const LANDSCAPE_RESOURCE: Readonly<Record<Landscape, Resource>> = {
  weide: 'wol',
  bergen: 'erts',
  bos: 'hout',
  rivierbedding: 'goud',
  akker: 'graan',
  heuvels: 'klei',
};

/**
 * De zes vlakken van de gebeurtenissendobbelsteen.
 * Verdeling (bevestigd, kennisbank 05 vraag 1): vier gebeurtenissen elk 1 vlak,
 * `gebeurteniskaart` staat op 2 vlakken.
 */
export const EVENT_FACES = [
  'riddertoernooi',
  'handelsvoordeel',
  'roofoverval',
  'goed-jaar',
  'gebeurteniskaart',
] as const;
export const EventFaceSchema = z.enum(EVENT_FACES);
export type EventFace = z.infer<typeof EventFaceSchema>;

/** De dobbelsteen zoals hij fysiek is: zes vlakken, vraagteken dubbel. */
export const EVENT_DIE_FACES: readonly EventFace[] = [
  'riddertoernooi',
  'handelsvoordeel',
  'roofoverval',
  'goed-jaar',
  'gebeurteniskaart',
  'gebeurteniskaart',
];

/** Opbrengstendobbelsteen: 1 t/m 6. */
export type ProductionRoll = 1 | 2 | 3 | 4 | 5 | 6;

/** Zitplaats aan tafel. Twee spelers, meer kent dit spel niet. */
export type Seat = 0 | 1;
export const SeatSchema = z.union([z.literal(0), z.literal(1)]);

/** Vaste spelconstanten uit de regels. */
export const LIMITS = {
  /** Winst bij 12 punten, direct (kennisbank 02 §3). */
  victoryPoints: 12,
  /** Maximaal aantal grondstoffen dat op één landschap kan liggen (§2.1). */
  resourcesPerLandscape: 3,
  /** Startgrootte van de hand; kan tijdens het spel stijgen (§2.3). */
  baseHandLimit: 3,
  /** Roofoverval treft je boven dit aantal onbeschermde grondstoffen (§2.1). */
  brigandThreshold: 7,
  /** Roofoverval telt niet in de eerste twee beurten (fysieke regels 1996). */
  brigandGraceTurns: 2,
  /**
   * De gedeelde voorraad bouwkaarten — "7 straten. 5 dorpen. 7 steden." (handboek
   * p. 16). Eén voorraad voor béíde spelers, wie het eerst komt bouwt. Hier stond een
   * grens per spéler (5/7/7), en die kende het boekje niet.
   */
  dorpenVoorraad: 5,
  stratenVoorraad: 7,
  stedenVoorraad: 7,
  /** Wat elke speler daarnaast al uit zijn startkaarten op tafel heeft (p. 16). */
  startDorpen: 2,
  startStraten: 1,
  // Landen kennen géén grens per speler: de enige grens is de gedeelde trekstapel van
  // elf (handboek p. 16). Hier stond `maxLandscapes: 11` als grens per spéler — die
  // kende het boekje niet.
  /** Kosten om een uitbouwstapel te doorzoeken bij het aanvullen van je hand (§2.3). */
  searchStackCost: 2,
} as const;

/** Bouwkosten uit popup.txt (kennisbank 02 §2.2). */
export const BUILD_COSTS = {
  straat: { klei: 2, hout: 1 },
  dorp: { graan: 1, hout: 1, wol: 1, klei: 1 },
  stad: { erts: 3, graan: 2 },
  citadel: { goud: 2, erts: 2, graan: 1 },
} as const satisfies Record<string, Partial<Record<Resource, number>>>;
